import { FunctionField, InPlaceEditorProps, Labeled, TextInput, Validator } from "react-admin";
import { AegleInPlaceEditor } from "./AegleInPlaceEditor";
import { InputAdornment, Typography } from "@mui/material";
import { validatePhone, formatPhoneNumber } from "@/utils/validators/inPlaceValidators";
import { ValidAny } from "@/types";

/**
 * In-place editor for US phone number fields.
 *
 * Display mode: formats the stored value as +1 (XXX) XXX-XXXX.
 * Edit mode: shows a +1 prefix adornment with a tel input, validates phone format,
 * and normalizes to (XXX) XXX-XXXX on save.
 */
const InPlacePhoneEditor = ({
  source,
  label,
  readOnly = false,
  validate,
  ...rest
}: {
  source: string;
  label?: string;
  readOnly?: boolean;
  validate?: Validator | Validator[];
} & Omit<InPlaceEditorProps, "input" | "source">) => {
  // Compose built-in phone validation with any user-supplied validators
  const effectiveValidate: Validator | Validator[] = validate
    ? [validatePhone, ...(Array.isArray(validate) ? validate : [validate])]
    : validatePhone;

  const displayField = (
    <FunctionField
      render={(record: ValidAny) => {
        const value = source.split(".").reduce((acc: ValidAny, k) => acc?.[k], record);
        if (!value) return "-";
        const formatted = formatPhoneNumber(value);
        return formatted ? `+1 ${formatted}` : "-";
      }}
      variant="body1"
      sx={{ display: "block", mb: 0.5 }}
    />
  );

  if (readOnly) {
    return <Labeled label={label}>{displayField}</Labeled>;
  }

  return (
    <Labeled label={label}>
      <AegleInPlaceEditor
        source={source}
        editor={
          <TextInput
            {...rest}
            source={source}
            type="tel"
            variant="standard"
            size="small"
            margin="none"
            label={false}
            helperText={effectiveValidate ? undefined : false}
            validate={effectiveValidate}
            parse={formatPhoneNumber}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Typography variant="body2" color="text.secondary">
                    +1
                  </Typography>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInput-root": { marginTop: 0 },
            }}
          />
        }
        {...rest}
      >
        {displayField}
      </AegleInPlaceEditor>
    </Labeled>
  );
};

export default InPlacePhoneEditor;
